import {
  Avatar,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Field, FieldProps, FormikProps } from 'formik';
import React, { useEffect } from 'react';
import { FormikKeyboardDatePicker, FormikNumberFormat, FormikTextField } from 'src/common/components/formik';
import { SCHEDULE_DAYS_OF_WEEK, SCHEDULE_RECURRENCE_TYPES } from 'src/features/reporting/constants';
import { MonthlyRunMode, RecurrenceType, ScheduledEventInput } from 'src/features/reporting/store/types';
import { Props } from '../../container';
import { formatStringArray, WO2Checkbox, WO2Select, createPivotDateTimeFromDate, fromSydneyDateTimeToPivotDate } from 'src/common';
import { NumberFormatValues } from 'react-number-format';
import { DateTime } from 'luxon';
import { FromEmailAddressType } from '../../store/types';
import { MONTHLY_RUN_MODE_OPTIONS } from '../../store/constants';
import { RunDatePreview } from '../RunDatePreview';

interface ScheduleStepProps extends Props {
  formikProps: FormikProps<ScheduledEventInput>;
  editing: boolean;
}

export const ScheduleStep = (props: ScheduleStepProps): JSX.Element => {
  const theme = useTheme();

  const recurrenceType = props.formikProps.values.recurrenceType;
  const { recurrenceRule } = props.formikProps.values;

  function handleRecurrenceRuleChange<T>(field: string, value: T) {
    props.formikProps.setFieldValue(`recurrenceRule.${field}`, value);
  }

  const resetEndOnDate = () => {
    const pivotEndOn = createPivotDateTimeFromDate(DateTime.now().plus({ weeks: 1 }).toJSDate()).toJSDate();
    props.formikProps.setFieldValue('endOn', pivotEndOn);
  };

  const handleDayClick = (checked: boolean, day: string) => {
    const current = recurrenceRule?.daysOfWeek || [];
    const newDays = checked ? [...current, day] : current.filter((d) => d !== day);
    handleRecurrenceRuleChange('daysOfWeek', newDays);

    if (newDays.length < SCHEDULE_DAYS_OF_WEEK.length) {
      props.formikProps.setFieldValue('recurrenceType', 'Weekly');
    }
  };

  useEffect(() => {
    const daysOfWeek = props.formikProps.values.recurrenceRule?.daysOfWeek ?? [];
    if (daysOfWeek.length === SCHEDULE_DAYS_OF_WEEK.length) {
      props.formikProps.setFieldValue('recurrenceType', 'Daily');
    }
  }, [props.formikProps.values.recurrenceRule?.daysOfWeek]);

  const hasEndOn = props.formikProps.values.endOn !== undefined && props.formikProps.values.endOn !== null;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Grid container direction="column" spacing={2}>
        <Grid item container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Field fullWidth name="name" component={FormikTextField} label="Name" showRequiredAsterisk />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Field fullWidth name="description" component={FormikTextField} label="Description" />
          </Grid>
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Field name="startOn" component={FormikKeyboardDatePicker} label="Starts on (Sydney time)" showRequiredAsterisk>
              {(fieldProps: FieldProps) => {
                return (
                  <FormikKeyboardDatePicker
                    label="Starts on (Sydney time)"
                    field={fieldProps.field}
                    form={fieldProps.form}
                    showRequiredAsterisk
                    fullWidth
                    type="date"
                    style={{}}
                    onChange={(date: DateTime | null) => {
                      if (date) {
                        props.formikProps.setFieldValue('startOn', fromSydneyDateTimeToPivotDate(date));
                      } else {
                        props.formikProps.setFieldValue('startOn', createPivotDateTimeFromDate(new Date()).toJSDate());
                      }
                    }}
                  />
                );
              }}
            </Field>
          </Grid>
          <Grid item xs={12} sm={4}>
            <WO2Checkbox
              label="Make Recurring"
              title="Make Recurring"
              checked={props.formikProps.values.recurrenceRule !== undefined && props.formikProps.values.recurrenceRule !== null}
              onChangeHandler={(value: boolean) => {
                if (!value) {
                  props.formikProps.setFieldValue('recurrenceType', 'Once');
                  props.formikProps.setFieldValue('recurrenceRule', undefined);
                  props.formikProps.setFieldValue('endOn', undefined);
                  props.formikProps.setFieldValue('occurrences', undefined);
                } else {
                  props.formikProps.setFieldValue('recurrenceType', 'Daily');
                  props.formikProps.setFieldValue('recurrenceRule', {
                    interval: 1,
                    daysOfWeek: SCHEDULE_DAYS_OF_WEEK,
                    dayOfMonth: null,
                    weekOfMonth: null,
                    monthOfYear: null,
                  });
                  resetEndOnDate();
                }
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      {props.formikProps.values.recurrenceRule !== undefined && props.formikProps.values.recurrenceRule !== null && (
        <Grid item container spacing={2} direction="column">
          <Grid item container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Field fullWidth component={FormikTextField} name="recurrenceRule.interval">
                {(fieldProps: FieldProps) => {
                  return (
                    <FormikNumberFormat
                      formikFieldProps={fieldProps}
                      fullWidth={true}
                      numberFormatProps={{
                        placeholder: '',
                        name: fieldProps.field.name,
                        label: 'Repeats every',
                        isNumericString: true,
                        allowNegative: false,
                        decimalScale: 0,
                        onValueChange: (values: NumberFormatValues) => {
                          props.formikProps.setFieldValue(fieldProps.field.name, values.value);
                        },
                      }}
                    />
                  );
                }}
              </Field>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <WO2Select
                  value={props.formikProps.values.recurrenceType}
                  fullWidth
                  onChange={(e) => {
                    const value = e.target.value as RecurrenceType;
                    props.formikProps.setFieldValue('recurrenceType', e.target.value);
                    props.formikProps.setFieldValue('recurrenceRule.daysOfWeek', []);
                    props.formikProps.setFieldValue('recurrenceRule.dayOfMonth', null);
                    props.formikProps.setFieldValue('recurrenceRule.weekOfMonth', null);
                    props.formikProps.setFieldValue('recurrenceRule.monthOfYear', null);
                    props.formikProps.setFieldValue('monthlyRunModeId', null);
                    props.formikProps.setFieldTouched('recurrenceRule.daysOfWeek', false);
                    props.formikProps.setFieldTouched('recurrenceRule.dayOfMonth', false);
                    props.formikProps.setFieldTouched('recurrenceRule.weekOfMonth', false);
                    props.formikProps.setFieldTouched('recurrenceRule.monthOfYear', false);
                    if (value === 'Daily') {
                      props.formikProps.setFieldValue('recurrenceRule.daysOfWeek', SCHEDULE_DAYS_OF_WEEK);
                    } else if (value === 'Weekly') {
                      props.formikProps.setFieldValue('recurrenceRule.daysOfWeek', [DateTime.now().weekdayLong]);
                    } else if (value === 'Monthly') {
                      props.formikProps.setFieldValue('recurrenceRule.dayOfMonth', DateTime.now().day);
                      props.formikProps.setFieldValue('monthlyRunModeId', MonthlyRunMode.ExactDay);
                    } else if (value === 'Yearly') {
                      props.formikProps.setFieldValue('recurrenceRule.dayOfMonth', DateTime.now().day);
                      props.formikProps.setFieldValue('recurrenceRule.monthOfYear', DateTime.now().month);
                    }
                  }}
                >
                  {Object.keys(SCHEDULE_RECURRENCE_TYPES).map((key, index) => (
                    <MenuItem value={SCHEDULE_RECURRENCE_TYPES[key]} key={index}>
                      {key}
                    </MenuItem>
                  ))}
                </WO2Select>
              </FormControl>
            </Grid>
          </Grid>
          {['Daily', 'Weekly', 'Monthly', 'Yearly'].includes(recurrenceType) && (
            <Grid item>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                  p: 2,
                }}
              >
                <Grid container spacing={2} direction="column">
                  {(() => {
                    switch (recurrenceType) {
                      case 'Daily':
                      case 'Weekly':
                        const daysOfWeek: string[] = recurrenceRule?.daysOfWeek ?? [];
                        const interval = props.formikProps.values.recurrenceRule?.interval;
                        if (interval && interval >= 2) {
                          return null;
                        }
                        return (
                          <Grid item container gap={1} padding={2} alignItems="center" flexWrap="wrap">
                            {SCHEDULE_DAYS_OF_WEEK.map((day, index) => (
                              <Grid
                                item
                                key={index}
                                sx={{
                                  padding: '0px !important',
                                  margin: 0,
                                  ':hover': {
                                    cursor: 'pointer',
                                  },
                                }}
                                onClick={() => handleDayClick(!daysOfWeek.includes(day), day)}
                              >
                                <Avatar
                                  sx={{
                                    backgroundColor: daysOfWeek.includes(day) ? theme.palette.primary.main : 'gainsboro',
                                  }}
                                >
                                  {day.charAt(0)}
                                </Avatar>
                              </Grid>
                            ))}
                            {daysOfWeek.length > 0 && (
                              <Grid item>
                                <Typography>Occurs every {formatStringArray(daysOfWeek)} (Sydney time)</Typography>
                              </Grid>
                            )}
                          </Grid>
                        );
                      case 'Monthly':
                        const monthlyRunMode = props.formikProps.values.monthlyRunModeId ?? MonthlyRunMode.ExactDay;
                        return (
                          <Grid item container spacing={2} alignItems="stretch">
                            <Grid item xs={12} sm={3}>
                              <FormControl fullWidth>
                                <InputLabel id="day-of-month">Day of Month</InputLabel>
                                <WO2Select
                                  value={props.formikProps.values.recurrenceRule?.dayOfMonth ?? DateTime.now().day}
                                  fullWidth
                                  labelId="day-of-month"
                                  label="Day of Month"
                                  onChange={(e) => {
                                    props.formikProps.setFieldValue('recurrenceRule.dayOfMonth', e.target.value);
                                  }}
                                >
                                  {Array.from({ length: 31 }, (_, i) => i + 1).map((value) => (
                                    <MenuItem value={value} key={value}>
                                      {value}
                                    </MenuItem>
                                  ))}
                                </WO2Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <FormControl>
                                <RadioGroup
                                  value={String(monthlyRunMode)}
                                  onChange={(_, value) => {
                                    props.formikProps.setFieldValue('monthlyRunModeId', Number(value));
                                  }}
                                >
                                  {MONTHLY_RUN_MODE_OPTIONS.map((option) => (
                                    <FormControlLabel
                                      key={option.value}
                                      value={String(option.value)}
                                      control={<Radio />}
                                      label={
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                          <Typography variant="body2">{option.label}</Typography>
                                          <Tooltip title={option.tooltip}>
                                            <InfoOutlined fontSize="small" color="action" />
                                          </Tooltip>
                                        </Box>
                                      }
                                    />
                                  ))}
                                </RadioGroup>
                              </FormControl>
                            </Grid>
                            <Grid item sx={{ display: 'flex' }}>
                              <Divider orientation="vertical" flexItem />
                            </Grid>
                            <Grid item xs={12} sm>
                              <RunDatePreview
                                previewScheduledEventRunDates={props.previewScheduledEventRunDates}
                                input={{
                                  startOn: props.formikProps.values.startOn,
                                  recurrenceType: props.formikProps.values.recurrenceType,
                                  recurrenceRule: props.formikProps.values.recurrenceRule,
                                  occurrences: props.formikProps.values.occurrences,
                                  endOn: props.formikProps.values.endOn,
                                  monthlyRunModeId: monthlyRunMode,
                                }}
                              />
                            </Grid>
                          </Grid>
                        );
                      case 'Yearly':
                        const monthOfYear = props.formikProps.values.recurrenceRule?.monthOfYear ?? DateTime.now().month;
                        const daysInSelectedMonth = DateTime.local(DateTime.now().year, monthOfYear).daysInMonth;

                        return (
                          <Grid item container spacing={2}>
                            <Grid item xs={12} sm={2}>
                              <FormControl fullWidth>
                                <InputLabel id="month-of-year">Month of Year</InputLabel>
                                <WO2Select
                                  value={monthOfYear}
                                  fullWidth
                                  labelId="month-of-year"
                                  label="Month of Year"
                                  onChange={(e) => {
                                    props.formikProps.setFieldValue('recurrenceRule.monthOfYear', e.target.value);
                                  }}
                                >
                                  {Array.from({ length: 12 }, (_, i) => i + 1).map((value) => (
                                    <MenuItem value={value} key={value}>
                                      {DateTime.fromObject({ month: value }).monthLong}
                                    </MenuItem>
                                  ))}
                                </WO2Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <FormControl fullWidth>
                                <InputLabel id="day-of-month">Day of Month</InputLabel>
                                <WO2Select
                                  value={props.formikProps.values.recurrenceRule?.dayOfMonth ?? DateTime.now().day}
                                  fullWidth
                                  labelId="day-of-month"
                                  label="Day of Month"
                                  onChange={(e) => {
                                    props.formikProps.setFieldValue('recurrenceRule.dayOfMonth', e.target.value);
                                  }}
                                >
                                  {Array.from({ length: daysInSelectedMonth }, (_, i) => i + 1).map((value) => (
                                    <MenuItem value={value} key={value}>
                                      {value}
                                    </MenuItem>
                                  ))}
                                </WO2Select>
                              </FormControl>
                            </Grid>
                          </Grid>
                        );
                    }
                  })()}
                </Grid>
              </Box>
              <Grid item sx={{ mt: 2 }}>
                <RadioGroup>
                  <FormControl>
                    <RadioGroup
                      defaultValue="endBy"
                      onChange={(_, value: string) => {
                        if (value === 'endBy') {
                          props.formikProps.setFieldValue('occurrences', 0);
                          resetEndOnDate();
                        } else if (value === 'endAfter') {
                          props.formikProps.setFieldValue('endOn', undefined);
                        }
                      }}
                    >
                      <Grid item container direction="column" spacing={2}>
                        <Grid item container alignItems="center" spacing={2}>
                          <Grid item xs={2}>
                            <FormControlLabel
                              value="endBy"
                              control={
                                <Radio
                                  checked={hasEndOn}
                                  onChange={(_, checked: boolean) => {
                                    if (checked) {
                                      resetEndOnDate();
                                    }
                                  }}
                                />
                              }
                              label="End by (Sydney time)"
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <Field name="endOn" component={FormikKeyboardDatePicker} label="" showRequiredAsterisk>
                              {(fieldProps: FieldProps) => {
                                return (
                                  <FormikKeyboardDatePicker
                                    label=""
                                    field={fieldProps.field}
                                    form={fieldProps.form}
                                    showRequiredAsterisk
                                    fullWidth
                                    type="date"
                                    style={{}}
                                    onChange={(date: DateTime | null) => {
                                      if (date) {
                                        props.formikProps.setFieldValue('endOn', fromSydneyDateTimeToPivotDate(date));
                                      } else {
                                        props.formikProps.setFieldValue('endOn', undefined);
                                      }
                                    }}
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                        </Grid>
                        <Grid item container alignItems="center" spacing={2}>
                          <Grid item xs={2}>
                            <FormControlLabel
                              control={
                                <Radio
                                  onChange={(_, checked: boolean) => {
                                    if (checked) {
                                      props.formikProps.setFieldValue('endOn', undefined);
                                    }
                                  }}
                                />
                              }
                              label="End after"
                              checked={!hasEndOn}
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <Field fullWidth component={FormikTextField} name="occurrences">
                              {(fieldProps: FieldProps) => {
                                return (
                                  <FormikNumberFormat
                                    formikFieldProps={fieldProps}
                                    fullWidth={true}
                                    numberFormatProps={{
                                      placeholder: '',
                                      name: fieldProps.field.name,
                                      label: '',
                                      isNumericString: true,
                                      allowNegative: false,
                                      decimalScale: 0,
                                      disabled: hasEndOn,
                                      onValueChange: (values: NumberFormatValues) => {
                                        props.formikProps.setFieldValue(fieldProps.field.name, values.value);
                                      },
                                    }}
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </RadioGroup>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
      <Grid item mt={2}>
        <Divider />
      </Grid>
      <Grid item container spacing={2}>
        <Grid item>
          <WO2Checkbox
            label="Email reports to clients"
            onChangeHandler={(value: boolean) => {
              if (value) {
                props.formikProps.setFieldValue('emailParameters', {
                  fromEmailAddressType: FromEmailAddressType.DASH,
                  ccEmailAddressTypes: [],
                  subject: '',
                  body: '',
                });
              } else {
                props.formikProps.setFieldValue('emailParameters', undefined);
              }
            }}
            checked={props.formikProps.values.emailParameters !== undefined}
          />
        </Grid>
        <Grid item>
          <WO2Checkbox
            label="Display Report on Investor Portal"
            onChangeHandler={(value: boolean) => {
              props.formikProps.setFieldValue('displayOnInvestorPortal', value);
            }}
            checked={props.formikProps.values.displayOnInvestorPortal}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
